import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { ChirpButton } from 'javascripts/components/chirp_button';
import { AudiobookItemButton } from 'javascripts/components/audiobook_item_button';

import { bubEvents } from 'javascripts/shared/events/bub_events';

import { ShoppingCartContext } from 'javascripts/contexts/shopping_cart_context';

import heartIcon from 'images/Heart_Active.png';
import heartIconActiveHovered from 'images/Heart_Pink_120.png';
import heartIconInactive from 'images/Heart_Inactive.png';

export function WishlistButton({
  audiobookId,
  inWishlist,
  condensed,
  inCart,
  onClick,
  trackingCategory,
  buttonStyle,
  buttonColor,
  hoverColor,
  small
}) {
  const { removeItemFromWishlist, addItemToWishlist } = useContext(ShoppingCartContext);

  function handleClick() {
    bubEvents.track('Wishlist button', 'Clicked button', {
      label: inWishlist ? 'Removed' : 'Added',
      value: audiobookId,
      properties: {
        tracking_category: trackingCategory,
      },
    });

    const action = inWishlist ? removeItemFromWishlist : addItemToWishlist;

    action({ id: audiobookId }, { inCart }).then(onClick);
  }

  function renderAudiobookListButton() {
    if (inWishlist) {
      return (
        <AudiobookItemButton
          label={condensed ? 'On Wishlist' : 'On Your Wishlist'}
          href='/wishlist'
          buttonColor={buttonColor}
          hoverColor={hoverColor}
        />
      );
    }
    return (
      <AudiobookItemButton
        attrs={{ 'data-shopping-cart': inCart ? 'remove' : null }}
        label={condensed ? 'Wishlist' : 'Save to Wishlist'}
        onClick={handleClick}
        buttonColor={buttonColor}
        hoverColor={hoverColor}
      />
    );
  }

  function renderIconButton() {
    return (
      <ChirpButton
        icon={inWishlist ? heartIcon : heartIconInactive}
        iconHover={inWishlist ? heartIconActiveHovered : heartIconInactive}
        iconSize='extraLarge'
        onClick={handleClick}
        iconAltText={inWishlist ? 'Remove from Wishlist' : 'Save to Wishlist'}
        displayType='iconOnly'
      />
    );
  }

  function renderModalButton() {
    if (inWishlist) {
      return <ChirpButton displayStyle='yellow' fullWidth href='/wishlist' label='See Wishlist' onClick={onClick} />;
    }
    return (
      <ChirpButton
        attrs={{ 'data-shopping-cart': inCart ? 'remove' : null }}
        displayStyle='yellow'
        fullWidth
        label='Save to Wishlist'
        onClick={handleClick}
      />
    );
  }

  function standardWishlistButtonStyles() {
    if (buttonStyle === 'defaultYellow') {
      return {
        displayStyle: 'yellow',
        displayType: 'button',
        fullWidth: true,
      };
    }

    if (buttonStyle === 'defaultGray') {
      return {
        displayStyle: 'gray',
        displayType: 'button',
        fullWidth: true,
      };
    }

    if (inWishlist) {
      return {
        displayStyle: 'pink',
        displayType: 'link',
        icon: heartIcon,
      };
    }

    return {
      displayStyle: 'gray',
      displayStyleHover: 'pink',
      displayType: 'link',
      icon: heartIconInactive,
      iconHover: heartIcon,
    };
  }

  function renderStandardWishlistButton() {
    const styles = standardWishlistButtonStyles();

    if (inWishlist) {
      return (
        <ChirpButton
          {...styles}
          label={condensed ? 'Wishlisted' : 'On Your Wishlist'}
          onClick={handleClick}
          fontSize={small ? 'small' : 'medium'}
        />
      );
    }

    return (
      <ChirpButton
        attrs={{ 'data-shopping-cart': inCart ? 'remove' : null }}
        {...styles}
        label={condensed ? 'Wishlist' : 'Save to Wishlist'}
        onClick={handleClick}
        fontSize={small ? 'small' : 'medium'}
      />
    );
  }

  if (buttonStyle === 'icon') {
    return renderIconButton();
  }
  if (buttonStyle === 'audiobookList') {
    return renderAudiobookListButton();
  }
  if (buttonStyle === 'modal') {
    return renderModalButton();
  }
  return renderStandardWishlistButton();
}

WishlistButton.propTypes = {
  audiobookId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  inWishlist: PropTypes.bool.isRequired,
  condensed: PropTypes.bool,
  inCart: PropTypes.bool,
  onClick: PropTypes.func,
  renderButton: PropTypes.func,
  additionalButtonProps: PropTypes.object,
  trackingCategory: PropTypes.string,
  buttonStyle: PropTypes.oneOf(['default', 'icon', 'audiobookList', 'modal', 'defaultYellow', 'defaultGray', 'stickyFooter']),
  buttonColor: PropTypes.string,
  hoverColor: PropTypes.string,
  small: PropTypes.bool,
};

WishlistButton.defaultProps = {
  onClick: _.noop,
  condensed: false,
  inCart: false,
  renderButton: ChirpButton,
  trackingCategory: null,
  buttonStyle: 'default',
  small: false,
};
